import { Controller } from "@hotwired/stimulus"

// The /events frame around its map, after justjoin.it.
//
// - The map is hidden with the round button in its corner and brought back with
//   the tab at the window's edge. The choice is remembered on this device,
//   because a reader who closed it to read a long list should not have to close
//   it on every visit. It is a convenience, so a browser that refuses storage
//   simply gets the map back.
// - On a phone the map is not beside the list. It opens over the page from the
//   list's toolbar and closes from its corner.
// - The list column is a Turbo frame. Opening an event swaps the event into it
//   while the map stays and moves to the event; going back swaps the list in
//   again where the reader left it, and the map returns to every event.
// - A card under the pointer lights its pin, and a pin clicked on the map
//   scrolls the list to its card.
//
// The map and the list talk through events, never through each other, so
// either works alone, and with no script every link here is an ordinary page.
const STORAGE_KEY = "events-map"
const PANEL = "events-panel"

export default class extends Controller {
  static targets = ["map", "types"]

  connect() {
    this.selected = (event) => this.#reveal(event.detail.id)
    this.cardClicks = (event) => this.#openFromMap(event)
    this.element.addEventListener("pixel-map:selected", this.selected)
    this.element.addEventListener("click", this.cardClicks)

    let stored = null
    try { stored = window.localStorage.getItem(STORAGE_KEY) } catch { /* storage refused */ }
    this.element.classList.toggle("events-page--no-map", stored === "hidden")

    this.#remember()
  }

  disconnect() {
    this.element.removeEventListener("pixel-map:selected", this.selected)
    this.element.removeEventListener("click", this.cardClicks)
    window.removeEventListener("scroll", this.scrolled)
  }

  hideMap() {
    if (this.element.classList.contains("events-page--map-open")) return this.closeMapSheet()

    this.element.classList.add("events-page--no-map")
    this.#store("hidden")
  }

  showMap() {
    this.element.classList.remove("events-page--no-map")
    this.#store("shown")
  }

  openMapSheet() {
    this.element.classList.add("events-page--map-open")
  }

  closeMapSheet() {
    this.element.classList.remove("events-page--map-open")
  }

  // "Show on map" on an event's tile. On a wide screen the map beside the
  // event moves to it, and comes back first if it was hidden. On a phone the
  // link's own anchor scrolls to the location map at the end of the event.
  showOnMap(event) {
    if (window.matchMedia("(max-width: 899px)").matches) return

    event.preventDefault()
    this.showMap()
    this.#mapController()?.focus(event.currentTarget.dataset.eventId)
  }

  hover(event) {
    this.#highlight(event.currentTarget.dataset.eventId)
  }

  unhover() {
    this.#highlight(null)
  }

  // Back to the list exactly as it was left, filters and scroll included.
  // Arriving at an event from outside there is no list to return to, and the
  // link's own address, the calendar, is followed into the frame instead.
  back(event) {
    if (!this.listUrl || !window.Turbo) return

    event.preventDefault()
    this.returning = true
    window.Turbo.visit(this.listUrl, { frame: PANEL, action: "advance" })
  }

  // A new page in the frame: an event, which the map centres on, or the list,
  // which the map draws whole again.
  panelLoaded(event) {
    if (event.target.id !== PANEL) return

    const panel = event.target.querySelector(".events-panel")
    if (panel?.dataset.title) document.title = panel.dataset.title

    const focus = panel?.dataset.eventFocus || null
    this.#mapController()?.focus(focus)
    // The type bar filters the list, so it goes while an event stands in the
    // list's place and comes back with the list.
    if (this.hasTypesTarget) this.typesTarget.hidden = Boolean(focus)

    if (focus) {
      this.closeMapSheet()
      window.scrollTo({ top: 0 })
    } else if (this.returning) {
      window.scrollTo({ top: this.listScroll || 0 })
    }
    this.returning = false
    this.#remember()
  }

  #remember() {
    if (!this.element.querySelector(`#${PANEL} [data-events-list]`)) return

    this.listUrl = window.location.href
    this.listScroll = 0
    window.removeEventListener("scroll", this.scrolled)
    this.scrolled = () => {
      if (this.element.querySelector(`#${PANEL} [data-events-list]`)) this.listScroll = window.scrollY
    }
    window.addEventListener("scroll", this.scrolled, { passive: true })
  }

  // The map's card links go into the frame too, so opening an event from a
  // pin keeps the map where it is rather than drawing it again.
  #openFromMap(event) {
    const link = event.target.closest(".events-page__map .pixel-map__card a[href]")
    if (!link || !window.Turbo || event.metaKey || event.ctrlKey || event.shiftKey) return

    event.preventDefault()
    window.Turbo.visit(link.href, { frame: PANEL, action: "advance" })
  }

  #highlight(id) {
    window.dispatchEvent(new CustomEvent("pixel-map:highlight", { detail: { id } }))
  }

  #mapController() {
    const element = this.hasMapTarget && this.mapTarget.querySelector("[data-controller~='pixel-map']")
    return element ? this.application.getControllerForElementAndIdentifier(element, "pixel-map") : null
  }

  #store(value) {
    try { window.localStorage.setItem(STORAGE_KEY, value) } catch { /* storage refused */ }
  }

  // A pin chosen on the map brings its card into view and lights it briefly.
  #reveal(id) {
    const card = this.element.querySelector(`[data-event-id="${CSS.escape(id)}"]`)
    if (!card) return

    card.scrollIntoView({ behavior: "smooth", block: "center" })
    card.classList.add("event-card--lit")
    window.clearTimeout(this.litTimer)
    this.litTimer = window.setTimeout(() => card.classList.remove("event-card--lit"), 1600)
  }
}
